if (!customElements.get('product-kit')) {
  customElements.define(
    'product-kit',
    class ProductKit extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          mode: '.m-product-kit__mode',
          slotsWrapper: '[data-kit-slots]',
          slot: '.m-product-kit__slot',
          optionButton: '.m-product-kit__option-button',
          validation: '[data-kit-validation]',
        };
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productId = this.dataset.productId;
        this.productFormId = this.dataset.productFormId;
        this.kitOptionIndex = Number(this.dataset.kitOptionIndex || 0);
        this.kitOptionName = this.dataset.kitOptionName || '';

        const initiallySelected = this.querySelector(`${this.selectors.mode}.is-selected`);
        this.mode = initiallySelected ? initiallySelected.dataset.kitMode : 'single';
        this.kitSize = initiallySelected ? Number(initiallySelected.dataset.kitSize || 1) : 1;

        this.slotSelections = {};
        this.kitSizeByMode = {};
        this.kitLabelByMode = {};
        this.querySelectorAll(this.selectors.mode).forEach((el) => {
          const modeKey = el.dataset.kitMode;
          const size = Number(el.dataset.kitSize || 1);
          this.kitSizeByMode[modeKey] = size;
          const title = el.querySelector('.m-product-kit__mode-title');
          this.kitLabelByMode[modeKey] = title ? title.textContent.trim() : modeKey;
        });
        this.querySelectorAll(this.selectors.slotsWrapper).forEach((wrapper) => {
          const modeKey = wrapper.dataset.kitSlotsFor;
          if (!modeKey) return;
          const slotCount = wrapper.querySelectorAll(this.selectors.slot).length;
          this.slotSelections[modeKey] = new Array(slotCount).fill(null);
        });

        try {
          this.variants = JSON.parse(this.querySelector('[data-kit-variants]').textContent);
        } catch (e) {
          this.variants = [];
        }

        this.form = document.getElementById(this.productFormId);
        this.submitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);

        this._bindModeToggle();
        this._bindSlotButtons();
        this._bindSubmitInterception();
      }

      _bindModeToggle() {
        this.querySelectorAll(this.selectors.mode).forEach((el) => {
          el.addEventListener('click', () => {
            const newMode = el.dataset.kitMode;
            if (newMode === this.mode) return;
            this.mode = newMode;
            this.kitSize = this.kitSizeByMode[newMode] || 1;

            this.querySelectorAll(this.selectors.mode).forEach((m) => m.classList.remove('is-selected'));
            el.classList.add('is-selected');
            const input = el.querySelector('input');
            if (input) input.checked = true;

            this.querySelectorAll(this.selectors.slotsWrapper).forEach((wrapper) => {
              wrapper.hidden = wrapper.dataset.kitSlotsFor !== newMode;
            });

            this._updateSubmitState();
          });
        });
      }

      _bindSlotButtons() {
        this.querySelectorAll(this.selectors.slotsWrapper).forEach((wrapper) => {
          const modeKey = wrapper.dataset.kitSlotsFor;
          if (!modeKey) return;
          wrapper.querySelectorAll(this.selectors.optionButton).forEach((btn) => {
            btn.addEventListener('click', (e) => {
              if (btn.disabled) return;
              e.preventDefault();
              const slotEl = btn.closest(this.selectors.slot);
              if (!slotEl) return;
              const slotIndex = Number(slotEl.dataset.kitSlot);
              const value = btn.dataset.kitOptionValue;

              slotEl
                .querySelectorAll(this.selectors.optionButton)
                .forEach((b) => b.classList.remove('is-selected'));
              btn.classList.add('is-selected');

              if (!this.slotSelections[modeKey]) this.slotSelections[modeKey] = [];
              this.slotSelections[modeKey][slotIndex] = value;
              this._updateSubmitState();
            });
          });
        });
      }

      _isSingle() {
        return this.mode === 'single';
      }

      _isKitFilled() {
        if (this._isSingle()) return true;
        const selections = this.slotSelections[this.mode] || [];
        const expected = this.kitSize || selections.length;
        if (selections.length < expected) return false;
        for (let i = 0; i < expected; i++) {
          if (selections[i] == null) return false;
        }
        return true;
      }

      _updateSubmitState() {
        if (!this.submitButton) return;
        const activeWrapper = this.querySelector(
          `[data-kit-slots-for="${this.mode}"]`
        );
        const validation = activeWrapper
          ? activeWrapper.querySelector(this.selectors.validation)
          : null;

        this.querySelectorAll(this.selectors.validation).forEach((v) => {
          v.hidden = true;
        });

        if (this._isSingle()) {
          this.submitButton.classList.remove('m-add-to-cart--kit-blocked');
          this.submitButton.removeAttribute('aria-disabled');
        } else {
          const filled = this._isKitFilled();
          this.submitButton.classList.toggle('m-add-to-cart--kit-blocked', !filled);
          this.submitButton.setAttribute('aria-disabled', !filled ? 'true' : 'false');
          if (validation) validation.hidden = filled;
        }
      }

      _bindSubmitInterception() {
        if (!this.form || !this.submitButton) return;

        this.submitButton.addEventListener(
          'click',
          (e) => {
            if (this._isSingle()) return;
            e.preventDefault();
            e.stopImmediatePropagation();
            if (!this._isKitFilled()) {
              const activeWrapper = this.querySelector(
                `[data-kit-slots-for="${this.mode}"]`
              );
              const validation = activeWrapper
                ? activeWrapper.querySelector(this.selectors.validation)
                : null;
              if (validation) validation.hidden = false;
              if (activeWrapper && typeof activeWrapper.scrollIntoView === 'function') {
                activeWrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
              return;
            }
            this._submitKit();
          },
          true
        );

        this.form.addEventListener(
          'submit',
          (e) => {
            if (this._isSingle()) return;
            e.preventDefault();
            e.stopImmediatePropagation();
            if (!this._isKitFilled()) return;
            this._submitKit();
          },
          true
        );
      }

      _getCurrentSelectedVariant() {
        const idInput = this.form.querySelector('[name="id"]');
        const selectedId = idInput ? Number(idInput.value) : null;
        if (selectedId) {
          const match = this.variants.find((v) => v.id === selectedId);
          if (match) return match;
        }
        return this.variants.find((v) => v.available) || this.variants[0] || null;
      }

      _findVariantForSlot(slotValue) {
        const base = this._getCurrentSelectedVariant();
        if (!base) return null;
        const desired = [...base.options];
        desired[this.kitOptionIndex] = slotValue;
        return this.variants.find(
          (v) => v.available && v.options.every((o, i) => o === desired[i])
        );
      }

      async _submitKit() {
        if (!this.submitButton) return;
        this.submitButton.classList.add('loading');
        this._loadingDisabled = !this.submitButton.hasAttribute('disabled');
        if (this._loadingDisabled) {
          this.submitButton.setAttribute('disabled', '');
        }

        const kitGroupId = `kit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const kitLabel = this.kitLabelByMode[this.mode] || `Kit de ${this.kitSize}`;
        const selections = (this.slotSelections[this.mode] || []).slice(0, this.kitSize);
        const items = [];
        for (const value of selections) {
          const variant = this._findVariantForSlot(value);
          if (!variant) {
            this._showError(
              `${this.kitOptionName || 'Opção'} indisponível para uma das peças do kit: ${value}`
            );
            this._resetButton();
            return;
          }
          items.push({
            id: variant.id,
            quantity: 1,
            properties: {
              _kit_group: kitGroupId,
              _kit_label: kitLabel,
            },
          });
        }

        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          })
        );

        const { MinimogSettings, MinimogStrings, MinimogTheme, MinimogEvents } = window;
        const config =
          typeof fetchConfig === 'function'
            ? fetchConfig('javascript')
            : {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/javascript',
                },
              };
        config.method = 'POST';
        config.body = JSON.stringify({
          items,
          sections: sectionsToBundle,
          sections_url: window.location.pathname,
        });

        try {
          const response = await fetch(MinimogSettings.routes.cart_add_url, config);
          const data = await response.json();

          if (data.status) {
            this._showError(data.description || 'Falha ao adicionar o kit ao carrinho.');
            this._resetButton();
            return;
          }

          const cartDrawer = document.querySelector('cart-drawer');
          if (MinimogSettings.use_ajax_atc) {
            if (cartDrawer && MinimogSettings.enable_cart_drawer) {
              if (MinimogTheme && MinimogTheme.Notification) {
                MinimogTheme.Notification.show({
                  target: cartDrawer.querySelector('m-cart-items'),
                  method: 'prepend',
                  type: 'success',
                  message: (MinimogStrings && MinimogStrings.itemAdded) || 'Adicionado ao carrinho',
                  delay: 400,
                });
              }
              if (typeof cartDrawer.open === 'function') cartDrawer.open();
            } else if (MinimogTheme && MinimogTheme.Notification) {
              MinimogTheme.Notification.show({
                target: document.body,
                method: 'appendChild',
                type: 'success',
                message: (MinimogStrings && MinimogStrings.itemAdded) || 'Adicionado ao carrinho',
                last: 3000,
                sticky: true,
              });
            }

            if (MinimogEvents && MinimogTheme && MinimogTheme.pubSubEvents) {
              MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, { cart: data });
            }
            document.dispatchEvent(
              new CustomEvent('product-ajax:added', { detail: { product: data } })
            );
          } else {
            window.location = MinimogSettings.routes.cart;
          }
        } catch (err) {
          console.error('Kit add error:', err);
          this._showError('Ocorreu um erro ao adicionar o kit ao carrinho.');
        } finally {
          this._resetButton();
        }
      }

      _resetButton() {
        if (!this.submitButton) return;
        this.submitButton.classList.remove('loading');
        if (this._loadingDisabled) {
          this.submitButton.removeAttribute('disabled');
          this._loadingDisabled = false;
        }
        this._updateSubmitState();
      }

      _showError(msg) {
        const wrapper = document.querySelector(
          `#MainProduct-${this.sectionId} .m-product-form-message`
        );
        if (wrapper && window.MinimogTheme && window.MinimogTheme.Notification) {
          window.MinimogTheme.Notification.show({
            target: wrapper,
            method: 'appendChild',
            type: 'warning',
            message: msg,
          });
        } else {
          alert(msg);
        }
      }
    }
  );
}
